import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as bookingService from "./booking.service";
import {
  ITempBookingPayload,
  IFinalOrderPayload,
  IUploadPaymentPayload,
  IOrderListItem,
  IOrderDetailsResponse,
  IAdminOrder,
  IGetAllOrdersPayload,
  ITempBookingListItem,
  ITempBookingListPayload,
} from "./bookingModel";

export const insertTempBookingAction = createAsyncThunk(
  "booking/insertTempBooking",
  async (payload: ITempBookingPayload, { rejectWithValue }) => {
    try {
      const response = await bookingService.insertTempBooking(payload);
      if (
        response &&
        (response.Status === 0 ||
          response.Status >= 400 ||
          response.status === false ||
          (response.Success !== undefined && !response.Success))
      ) {
        const errorMsg =
          response.ErrorMessage ||
          response.Details ||
          response.Message ||
          response.message ||
          "Failed to create temporary booking.";
        return rejectWithValue(errorMsg);
      }
      return response;
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.ErrorMessage ||
        error?.response?.data?.Details ||
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create temporary booking.";
      return rejectWithValue(errorMsg);
    }
  },
);

export const createFinalOrderAction = createAsyncThunk(
  "booking/createFinalOrder",
  async (payload: IFinalOrderPayload) => {
    const response = await bookingService.createFinalOrder(payload);
    return response;
  },
);

export const uploadPaymentImagesAction = createAsyncThunk(
  "booking/uploadPaymentImages",
  async (payload: IUploadPaymentPayload) => {
    const response = await bookingService.uploadPaymentImages(payload);
    return response;
  },
);

export const getCustomerOrdersAction = createAsyncThunk(
  "booking/getCustomerOrders",
  async (payload: { CustomerID: number }) => {
    const response = await bookingService.getCustomerOrders(payload);
    return response;
  },
);

export const getOrderDetailsAction = createAsyncThunk(
  "booking/getOrderDetails",
  async (payload: { OrderID: number }) => {
    const response = await bookingService.getOrderDetails(payload);
    return response;
  },
);

export const getAllOrdersAction = createAsyncThunk(
  "booking/getAllOrders",
  async (payload: IGetAllOrdersPayload) => {
    const response = await bookingService.getAllOrders(payload);
    return response;
  },
);

export const createPaymentOrderAction = createAsyncThunk(
  "booking/createPaymentOrder",
  async (payload: { Amount: number; Currency?: string }) => {
    const response = await bookingService.createPaymentOrder(payload);
    return response;
  },
);

export const verifyPaymentAction = createAsyncThunk(
  "booking/verifyPayment",
  async (payload: {
    RazorpayOrderId: string;
    RazorpayPaymentId: string;
    RazorpaySignature: string;
    booking_no: string;
  }) => {
    const response = await bookingService.verifyPayment(payload);
    return response;
  },
);


export const getTempBookingListAction = createAsyncThunk(
  "booking/getTempBookingList",
  async (payload?: ITempBookingListPayload) => {
    const response = await bookingService.getTempBookingList(payload);
    return response;
  },
);

interface BookingState {
  loading: boolean;
  error: string | null;
  tempID: number | null;
  orderID: number | null;
  bookingSuccess: boolean;
  paymentSuccess: boolean;
  customerOrders: IOrderListItem[];
  currentOrderDetails: IOrderDetailsResponse | null;
  fetchingOrders: boolean;
  fetchingDetails: boolean;
  adminOrders: IAdminOrder[];
  adminTotalCount: number;
  tempBookings: ITempBookingListItem[];
  fetchingTempBookings: boolean;
  tempBookingsError: string | null;
  adminOrderFilters: {
    FullName: string;
    MobileNo: string;
    OrderStatus: string;
  };
}

const initialState: BookingState = {
  loading: false,
  error: null,
  tempID: null,
  orderID: null,
  bookingSuccess: false,
  paymentSuccess: false,
  customerOrders: [],
  currentOrderDetails: null,
  fetchingOrders: false,
  fetchingDetails: false,
  adminOrders: [],
  adminTotalCount: 0,
  tempBookings: [],
  fetchingTempBookings: false,
  tempBookingsError: null,
  adminOrderFilters: {
    FullName: "",
    MobileNo: "",
    OrderStatus: "",
  },
};

const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    clearBookingState: (state) => {
      state.loading = false;
      state.error = null;
      state.tempID = null;
      state.orderID = null;
      state.bookingSuccess = false;
      state.paymentSuccess = false;
    },
    setAdminOrderFilters: (state, action) => {
      state.adminOrderFilters = {
        ...state.adminOrderFilters,
        ...action.payload,
      };
    },
    clearAdminOrderFilters: (state) => {
      state.adminOrderFilters = {
        FullName: "",
        MobileNo: "",
        OrderStatus: "",
      };
    },
  },
  extraReducers: (builder) => {
    // Insert Temp Booking
    builder.addCase(insertTempBookingAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(insertTempBookingAction.fulfilled, (state, action) => {
      state.loading = false;
      state.tempID = action.payload?.Data?.TempID || action.payload?.TempID;
      state.error = null;
    });
    builder.addCase(insertTempBookingAction.rejected, (state, action) => {
      state.loading = false;
      state.error =
        (typeof action.payload === "string" ? action.payload : null) ||
        action.error.message ||
        "Failed to insert temp booking";
    });

    // Create Final Order
    builder.addCase(createFinalOrderAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createFinalOrderAction.fulfilled, (state, action) => {
      state.loading = false;
      const data = action.payload?.Data || action.payload;
      state.orderID = data?.OrderID || data?.ID || null;
      state.bookingSuccess = true;
      state.error = null;
    });
    builder.addCase(createFinalOrderAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to create final order";
    });

    // Upload Payment Images
    builder.addCase(uploadPaymentImagesAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(uploadPaymentImagesAction.fulfilled, (state) => {
      state.loading = false;
      state.paymentSuccess = true;
      state.error = null;
    });
    builder.addCase(uploadPaymentImagesAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to upload payment images";
    });

    // Get Customer Orders
    builder.addCase(getCustomerOrdersAction.pending, (state) => {
      state.fetchingOrders = true;
      state.error = null;
    });
    builder.addCase(getCustomerOrdersAction.fulfilled, (state, action) => {
      state.fetchingOrders = false;
      state.customerOrders =
        action.payload?.Data?.Orders || action.payload?.Orders || [];
      state.error = null;
    });
    builder.addCase(getCustomerOrdersAction.rejected, (state, action) => {
      state.fetchingOrders = false;
      state.error = action.error.message || "Failed to fetch customer orders";
    });

    // Get Order Details
    builder.addCase(getOrderDetailsAction.pending, (state) => {
      state.fetchingDetails = true;
      state.error = null;
    });
    builder.addCase(getOrderDetailsAction.fulfilled, (state, action) => {
      state.fetchingDetails = false;
      state.currentOrderDetails =
        action.payload?.Data || action.payload || null;
      state.error = null;
    });
    builder.addCase(getOrderDetailsAction.rejected, (state, action) => {
      state.fetchingDetails = false;
      state.error = action.error.message || "Failed to fetch order details";
    });

    // Get All Orders (Admin)
    builder.addCase(getAllOrdersAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllOrdersAction.fulfilled, (state, action) => {
      state.loading = false;
      const data = action.payload?.Data || action.payload;
      state.adminOrders = data?.Orders || [];
      state.adminTotalCount = data?.Orders?.[0]?.ItemCount || 0;
      state.error = null;
    });
    builder.addCase(getAllOrdersAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch all orders";
    });

    // Get Temp Booking List (Admin)
    builder.addCase(getTempBookingListAction.pending, (state) => {
      state.fetchingTempBookings = true;
      state.tempBookingsError = null;
    });
    builder.addCase(getTempBookingListAction.fulfilled, (state, action) => {
      state.fetchingTempBookings = false;
      const data = action.payload?.Data || action.payload;
      state.tempBookings = Array.isArray(data) ? data : [];
      state.tempBookingsError = null;
    });
    builder.addCase(getTempBookingListAction.rejected, (state, action) => {
      state.fetchingTempBookings = false;
      state.tempBookings = [];
      state.tempBookingsError =
        action.error.message || "Failed to fetch temp bookings";
    });

    builder.addCase("auth/logoutAll", (state) => {
      state.loading = false;
      state.error = null;
      state.tempID = null;
      state.orderID = null;
      state.bookingSuccess = false;
      state.paymentSuccess = false;
      state.customerOrders = [];
      state.currentOrderDetails = null;
      state.fetchingOrders = false;
      state.fetchingDetails = false;
    });
  },
});

export const {
  clearBookingState,
  setAdminOrderFilters,
  clearAdminOrderFilters,
} = bookingSlice.actions;
export default bookingSlice.reducer;
